import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection, query, where, orderBy, onSnapshot,
  doc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';
import { useAuth } from '../context/AuthContext';

export function useUnreadFeedback() {
  const { companyId, profile, user, isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [seenOverride, setSeenOverride] = useState(null);

  useEffect(() => {
    if (!isAdmin || !companyId) {
      setItems([]);
      return;
    }
    const q = query(
      collection(db, COLLECTIONS.FEEDBACK),
      where('companyId', '==', companyId),
      orderBy('createdAt', 'desc'),
    );
    return onSnapshot(q, (snap) => {
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }, () => setItems([]));
  }, [companyId, isAdmin]);

  const lastSeenAt = seenOverride ?? profile?.feedbackLastSeenAt?.toDate?.() ?? null;

  const unreadCount = useMemo(() => {
    if (!isAdmin) return 0;
    return items.filter((fb) => {
      const created = fb.createdAt?.toDate?.();
      if (!created) return false;
      if (!lastSeenAt) return true;
      return created > lastSeenAt;
    }).length;
  }, [items, lastSeenAt, isAdmin]);

  const markAsRead = useCallback(async () => {
    if (!user?.uid || !isAdmin) return;
    setSeenOverride(new Date());
    try {
      await updateDoc(doc(db, COLLECTIONS.USERS, user.uid), {
        feedbackLastSeenAt: serverTimestamp(),
      });
    } catch {
      setSeenOverride(null);
    }
  }, [user, isAdmin]);

  return { unreadCount, markAsRead };
}
